import type { useNavigate } from '@change-corgi/core-react/router';
import type { useTracking } from '@change-corgi/core-react/tracking';

export type SharingToolsTrackingContext = 'petition_dashboard_grow_petition_v1' | 'petition_dashboard_grow_petition_v2';

export type NavConfig = {
	url: (slug: string) => string;
	content: string;
	cta: 'button' | 'link' | 'card';
};

export type NavHandlerContext = {
	slug: string;
	navigate: ReturnType<typeof useNavigate>;
	track: ReturnType<typeof useTracking>;
	trackingContext: SharingToolsTrackingContext;
};

const CLICK_EVENT_NAME = 'petition_manage_grow_your_petition_click';
const CTA_CARD_TYPE = 'card';

function trackClickEvent(
	track: ReturnType<typeof useTracking>,
	trackingContext: SharingToolsTrackingContext,
	content: string,
	cta: 'button' | 'link' | 'card',
): void {
	// Defer to ensure petitions_dashboard_view event fires first (from useLayoutEffect in Layout)
	setTimeout(() => {
		void track(CLICK_EVENT_NAME, {
			context: trackingContext,
			content,
			cta,
		});
	}, 100);
}

export const NAV_CONFIGS = {
	groupRecommendations: {
		url: (slug: string) => `/p/${slug}/dashboard/share#recommendations-section`,
		content: 'recommended_groups',
		cta: CTA_CARD_TYPE,
	},
	shareVisual: {
		url: (slug: string) => `/p/${slug}/dashboard/share#social-media-content-container`,
		content: 'social_content',
		cta: CTA_CARD_TYPE,
	},
	posters: {
		url: (slug: string) => `/p/${slug}/dashboard/share#share_qrcode_module`,
		content: 'poster',
		cta: CTA_CARD_TYPE,
	},
	allTools: { url: (slug: string) => `/p/${slug}/dashboard/share`, content: 'all', cta: 'link' as const },
	readyToSharePosts: {
		url: (slug: string) => `/p/${slug}/dashboard/share#ready-to-share-posts`,
		content: 'rts_posts',
		cta: CTA_CARD_TYPE,
	},
	editPetition: {
		url: (slug: string) => `/p/${encodeURIComponent(slug)}/edit`,
		content: 'edit_petition',
		cta: 'button' as const,
	},
} satisfies Record<string, NavConfig>;

export function createNavigationHandler(config: NavConfig, context: NavHandlerContext): () => void {
	return () => {
		context.navigate(config.url(context.slug));
		trackClickEvent(context.track, context.trackingContext, config.content, config.cta);
	};
}
